import { Shield, Wind, Code, Zap, Layers, Box, MessageSquare, BarChart3, Sparkles, AlertTriangle, Smile } from "lucide-react"

export function ReactTailwindFeaturesPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Key Features</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          The React + Tailwind CSS template comes packed with production-ready features including 
          utility-first styling, clean architecture, developer-friendly tooling, and advanced UI components.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <FeatureCard
          icon={<MessageSquare className="h-8 w-8 text-primary" />}
          title="Complete Chat Application"
          items={[
            "Direct & group chat conversations",
            "Message reactions with emoji picker",
            "Pin important messages",
            "File attachments with upload support",
            "Real-time online/offline status indicators",
            "Unread message badges",
            "Search conversations",
            "Add users to group chats",
          ]}
        />

        <FeatureCard
          icon={<BarChart3 className="h-8 w-8 text-primary" />}
          title="Admin Dashboard Components"
          items={[
            "MetricCard with trend indicators",
            "DataTable with search & sorting",
            "ChartCard with bar & line charts",
            "ActivityFeed with timeline",
            "QuickActions button grid",
            "ProgressBar with variants",
            "Badge with status colors",
            "SimpleChart visualizations",
          ]}
        />

        <FeatureCard
          icon={<Sparkles className="h-8 w-8 text-primary" />}
          title="Enhanced UX Features"
          items={[
            "Resizable sidebar with drag handle",
            "Enhanced header with search & notifications",
            "Smooth animations and transitions",
            "Backdrop blur effects",
            "Hover states on all interactive elements",
            "Mobile-first responsive design",
            "Theme toggle with rotation animation",
            "User profile with online status",
          ]}
        />

        <FeatureCard
          icon={<AlertTriangle className="h-8 w-8 text-primary" />}
          title="Error Handling & Auth"
          items={[
            "Error boundary for crash handling",
            "Custom 404 not found page",
            "Login page with validation",
            "Signup page with password matching",
            "Global confirmation dialog",
            "Global loader with nested states",
            "Lazy loading with Suspense",
            "Error pages with recovery options",
          ]}
        />

        <FeatureCard
          icon={<Smile className="h-8 w-8 text-primary" />}
          title="Global Components"
          items={[
            "EmojiPicker with emoji-mart integration",
            "Modal/Dialog with multiple sizes",
            "Confirmation dialog with variants",
            "Loading spinner with messages",
            "Theme-aware components",
            "Keyboard shortcuts (ESC, Enter)",
            "Click-outside to close",
            "Accessible ARIA labels",
          ]}
        />
        
        <FeatureCard
          icon={<Wind className="h-8 w-8 text-primary" />}
          title="Tailwind CSS Utility-First"
          items={[
            "Utility-first CSS for rapid development",
            "JIT compiler for instant style generation",
            "PurgeCSS for minimal production bundle",
            "Custom design system with theme variables",
            "Dark mode with class-based switching",
            "Responsive utilities for all breakpoints",
          ]}
        />
        
        <FeatureCard
          icon={<Code className="h-8 w-8 text-primary" />}
          title="TypeScript & Development"
          items={[
            "Full TypeScript support with strict mode",
            "Type-safe API services and stores",
            "Auto-completion and IntelliSense",
            "Path aliases (@/) for clean imports",
            "ESLint configuration for code quality",
            "Hot Module Replacement (HMR) with Vite",
          ]}
        />
        
        <FeatureCard
          icon={<Layers className="h-8 w-8 text-primary" />}
          title="Clean Architecture"
          items={[
            "Layout → Page → View separation",
            "Modular component organization",
            "Service layer for API communication",
            "Custom hooks for reusable logic",
            "Centralized constants and types",
            "Utility functions for common operations",
          ]}
        />
        
        <FeatureCard
          icon={<Zap className="h-8 w-8 text-primary" />}
          title="State Management"
          items={[
            "Zustand for lightweight global state",
            "Separate stores for auth, theme, and UI",
            "Persist middleware for state persistence",
            "DevTools integration for debugging",
            "Type-safe store definitions",
            "Custom hooks for store access",
          ]}
        />
        
        <FeatureCard
          icon={<Shield className="h-8 w-8 text-primary" />}
          title="API Integration"
          items={[
            "Axios for HTTP requests",
            "Request/response interceptors",
            "Automatic token management",
            "Error handling and retry logic",
            "Type-safe API service methods",
            "Sample Product service with full CRUD",
            "Custom React hooks for data",
            "Mock API support for development",
          ]}
        />
        
        <FeatureCard
          icon={<Box className="h-8 w-8 text-primary" />}
          title="Ready-to-Use Pages"
          items={[
            "Dashboard with statistics and charts",
            "Chat page with full messaging features",
            "Users management with data table",
            "Analytics with visualizations",
            "Settings page with form handling",
            "Login and Signup authentication pages",
            "Component showcase page",
            "Error and loading states throughout",
          ]}
        />
      </div>

      <div className="bg-muted/30 rounded-xl p-8 space-y-6 mt-12">
        <h2 className="text-2xl font-bold">Why Choose React + Tailwind CSS?</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Maximum Flexibility</h3>
            <p className="text-muted-foreground leading-relaxed">
              Tailwind's utility-first approach gives you complete control over your designs 
              without the constraints of pre-built components. Style exactly what you need, 
              when you need it.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Tiny Bundle Size</h3>
            <p className="text-muted-foreground leading-relaxed">
              PurgeCSS automatically removes unused styles in production. Only the CSS you 
              actually use makes it to the final bundle, resulting in incredibly small file sizes.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Lightning Fast Development</h3>
            <p className="text-muted-foreground leading-relaxed">
              Vite provides instant server start and blazing fast HMR. Combined with Tailwind's 
              JIT compiler, you see style changes immediately without any build step delay.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Production Ready</h3>
            <p className="text-muted-foreground leading-relaxed">
              The template includes everything you need: error handling, loading states, API 
              integration, state management, and responsive design. Start building features 
              immediately.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-card border rounded-xl p-8 space-y-6">
        <h2 className="text-2xl font-bold">Component Library</h2>
        <p className="text-muted-foreground leading-relaxed">
          The template comes with a comprehensive set of custom-built components using pure Tailwind CSS, 
          plus dashboard, chat, and global utility components.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <UtilityCategory
            title="Custom UI Components"
            items={["Button", "Card", "Input", "Select", "Modal", "Avatar", "Badge", "Switch", "Spinner"]}
          />
          <UtilityCategory
            title="Dashboard Components"
            items={["MetricCard", "DataTable", "ChartCard", "SimpleChart", "ActivityFeed", "QuickActions", "ProgressBar"]}
          />
          <UtilityCategory
            title="Chat Components"
            items={["ChatList", "ChatMessage", "ChatInput", "ChatHeader", "PinnedMessage", "MessageReactions", "AddUserModal"]}
          />
          <UtilityCategory
            title="Global Components"
            items={["EmojiPicker", "Modal", "ConfirmationDialog", "GlobalLoader", "ErrorBoundary", "ThemeToggle"]}
          />
          <UtilityCategory
            title="Layout Components"
            items={["AppLayout", "Sidebar (Resizable)", "Header (Enhanced)", "AuthLayout", "ErrorLayout"]}
          />
          <UtilityCategory
            title="Pages"
            items={["Dashboard", "Chat", "Users", "Analytics", "Settings", "Login", "Signup", "NotFound"]}
          />
        </div>
      </div>

      <div className="bg-primary/5 border-l-4 border-primary rounded-r-xl p-8 space-y-4 mt-8">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          New in This Update
        </h2>
        <div className="space-y-4 text-muted-foreground leading-relaxed">
          <p>
            <strong className="text-foreground">Complete Chat System:</strong> A full-featured chat application with direct 
            and group conversations, message reactions, pinned messages, file attachments, and an emoji picker.
          </p>
          <p>
            <strong className="text-foreground">Dashboard Components:</strong> Professional admin dashboard components including 
            metric cards with trends, sortable data tables, charts, activity feeds, and progress bars.
          </p>
          <p>
            <strong className="text-foreground">Enhanced UX:</strong> Resizable sidebar with persistent width, enhanced header 
            with search and notifications, smooth animations, and improved mobile responsiveness.
          </p>
          <p>
            <strong className="text-foreground">Production Features:</strong> Error boundaries, 404 pages, authentication pages, 
            global confirmation dialogs, loading states, and a complete Product API service example.
          </p>
        </div>
      </div>
    </div>
  )
}

function FeatureCard({ 
  icon, 
  title, 
  items 
}: { 
  icon: React.ReactNode
  title: string
  items: string[]
}) {
  return (
    <div className="bg-card border rounded-xl p-6 space-y-4">
      <div className="flex items-center gap-3">
        {icon}
        <h3 className="text-xl font-semibold">{title}</h3>
      </div>
      <ul className="space-y-3">
        {items.map((item, index) => (
          <li key={index} className="flex gap-3 text-sm text-muted-foreground">
            <span className="text-primary shrink-0">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function UtilityCategory({ 
  title, 
  items 
}: { 
  title: string
  items: string[]
}) {
  return (
    <div className="bg-muted/30 rounded-lg p-4 space-y-3">
      <h4 className="font-semibold">{title}</h4>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="text-sm text-muted-foreground flex gap-2">
            <span className="text-primary">→</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
